// Trash bin (v122). Lists soft-deleted artifacts + repos with
// Restore and Purge-now actions. Items are auto-purged 30 days
// after deletion by the reaper goroutine; the "expires in" badge
// shows the countdown.

import { Trans, useTranslation } from 'react-i18next';
import { Trash2, RotateCcw, Box, Package, AlertTriangle, FolderOpen } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useTrash, restoreTrash, purgeTrash, type TrashItem } from '../api/trash';
import { useWhoami } from '../api/client';

function daysLeft(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function expiryTone(days: number): 'danger' | 'warning' | 'neutral' {
  if (days <= 3) return 'danger';
  if (days <= 7) return 'warning';
  return 'neutral';
}

function kindBadge(kind: TrashItem['kind'], t: (key: string) => string) {
  if (kind === 'repo') {
    return (
      <Badge tone="nebula">
        <Box className="h-3 w-3" />
        {t('trash.kinds.repo')}
      </Badge>
    );
  }
  if (kind === 'project') {
    return (
      <Badge tone="warning">
        <FolderOpen className="h-3 w-3" />
        {t('trash.kinds.project')}
      </Badge>
    );
  }
  return (
    <Badge tone="accent">
      <Package className="h-3 w-3" />
      {t('trash.kinds.artifact')}
    </Badge>
  );
}

export function Trash() {
  const { t } = useTranslation();
  const { data, mutate } = useTrash();
  const { user } = useWhoami();
  // Permanent deletion before the 30-day reaper is org-admin only (backend
  // enforces it too). A project owner can restore but not purge early.
  const canPurge = !!(user?.roles?.includes('org_admin') || user?.roles?.includes('admin'));

  const onRestore = async (item: TrashItem) => {
    try {
      await restoreTrash(item.kind, item.id);
    } catch (e) {
      alert(t('trash.restoreFailed', { error: String(e) }));
    }
    await mutate();
  };

  const onPurge = async (item: TrashItem) => {
    const label = t(`trash.purgeLabels.${item.kind}`);
    if (!confirm(t('trash.confirmPurge', { label, title: item.title }))) {
      return;
    }
    try {
      await purgeTrash(item.kind, item.id);
    } catch (e) {
      alert(t('trash.purgeFailed', { error: String(e) }));
    }
    await mutate();
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-warning/80 mb-2">{t('trash.eyebrow')}</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{t('trash.title')}</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          <Trans i18nKey="trash.intro" components={[<span key={0} className="text-fg font-medium" />]} />
        </p>
      </div>

      <Card>
        <CardHeader
          title={t('trash.table.title', { count: data?.length ?? 0 })}
          description={t('trash.table.description')}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('trash.table.kind')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('trash.table.item')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('trash.table.deletedBy')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('trash.table.deleted')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('trash.table.expires')}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data.map((item) => {
                  const days = daysLeft(item.expires_at);
                  return (
                    <tr key={`${item.kind}-${item.id}`} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                      <td className="px-5 py-3">{kindBadge(item.kind, t)}</td>
                      <td className="px-5 py-3">
                        <p className="text-fg font-mono text-xs break-all max-w-md">{item.title}</p>
                        <p className="text-fg-muted text-xs">{item.subtitle}</p>
                      </td>
                      <td className="px-5 py-3 text-fg-muted text-xs">{item.deleted_by || '—'}</td>
                      <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                        {new Date(item.deleted_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-xs whitespace-nowrap">
                        <Badge tone={expiryTone(days)}>
                          {days === 0 ? t('trash.expiry.today') : t('trash.expiry.inDays', { count: days })}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => onRestore(item)}
                            title={t('trash.actions.restore')}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-success/10 hover:text-success"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          {canPurge && (
                            <button
                              type="button"
                              onClick={() => onPurge(item)}
                              title={t('trash.actions.purge')}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                    {t('trash.table.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
